import { useRef } from "react";
import * as yup from "yup";
import { Formik } from "formik";
import { FloatingLabel, Form, Button as BSButton, Col, Row } from "react-bootstrap";
import ReactTextareaAutosize from "react-textarea-autosize";
import { useSelector } from "react-redux";
import { colorPrimary, RootState } from "../../app";
import { ServiceTypeModel } from "../../data/entities";
import { PermissionDefinitions } from "../../security";
import { ServiceTypeService } from "../../services";
import { usePageActions } from "../";
import { useAsync, useCRUDHandlers, usePermission, useSetPageTitle, useTenantFeatures, useUrlParams } from "../../hooks";
import { Button, Card, IconAndText, IconDefinitions } from "../../components";
import { ColorGradient } from "@progress/kendo-react-all";

export function ServiceTypeDetailsPage() {

  const id = parseInt(useUrlParams("id"));
  const serviceTypeService = new ServiceTypeService();
  const { editItem } = useCRUDHandlers<ServiceTypeModel>(serviceTypeService, "Service Type");
  const submitRef = useRef<HTMLButtonElement>(null);
  const { value: serviceType, error, loading } = useAsync(() => serviceTypeService.get(id), [id]);

  const tenantId = useSelector((state: RootState) => state.authentication.tenantId);
  const { manageServiceTypes } = useTenantFeatures();
  const canEditServiceType = usePermission(PermissionDefinitions.serviceType.edit(tenantId!)) && manageServiceTypes;

  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    description: yup.string().notRequired(),
  });

  useSetPageTitle(serviceType?.name ?? "", [serviceType]);
  usePageActions(
    canEditServiceType && (
      <Button onClick={() => submitRef.current?.click()}>
        <IconAndText iconName={IconDefinitions.save} text="Save" />
      </Button>
    ),
    [canEditServiceType]
  );

  if (error) return <div>Error loading service type: {error.message}</div>;
  if (!serviceType || loading) return <></>;

  return (
    <Card title="Details">
      <Formik
        validationSchema={validationSchema}
        initialValues={
          { 
            name: serviceType.name,
            description: serviceType.description,
            color: serviceType.color
          }}
        onSubmit={(values) => {
          const body = {
            ...serviceType,
            ...values,
          } as ServiceTypeModel;
          editItem(body);
        }}
      >
        {({ values, errors, touched, setFieldValue, handleChange, handleSubmit }) => { 
        return(
          <Form 
            onSubmit={handleSubmit} 
            noValidate    
            onClick={(e) => {
              if ((e.target as Element).closest('.k-colorgradient-toggle-mode')) {
              e.preventDefault();
            }}}
          >
              <Row>
                <Col>
                  <FloatingLabel label="Name" className="mb-3">
                    <Form.Control
                        type="string"
                        placeholder="Name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        disabled={!canEditServiceType}
                        isInvalid={touched.name && !!errors.name}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                  </FloatingLabel>
                </Col>
              </Row>
              <Row>
                <Col>
                  <FloatingLabel label="Description" className="mb-3">
                    <ReactTextareaAutosize
                      minRows={4}
                      className="form-control"
                      placeholder="Description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      disabled={!canEditServiceType}
                    />
                  </FloatingLabel>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group controlId="color" className="mb-3">
                    <Form.Label className="small text-body-secondary mb-1">Colour</Form.Label>
                    <div>
                        <ColorGradient
                          value={values.color ?? colorPrimary}
                          onChange={(e) => {setFieldValue("color", e.value)}}
                          disabled={!canEditServiceType}
                          format="hex"
                          />
                    </div>
                  </Form.Group>
                </Col>
            </Row>
            <BSButton ref={submitRef} type="submit" className="d-none" />
          </Form>
        )}
      }
      </Formik>
    </Card>
  );
}

export default ServiceTypeDetailsPage;

